/**
 * Detection and recovery for failed dynamic-import / code-split chunk loads.
 *
 * Watchtower lazy-loads every route via React Router's `lazy: () => import(...)`.
 * When a new version is deployed, the content-hashed chunk files referenced by an
 * already-open tab are deleted, so the next navigation to a not-yet-loaded route
 * throws a chunk-load error. A full page reload fetches the fresh `index.html`
 * and its current chunks, recovering transparently.
 *
 * The detector must cover the different messages browsers use for this single
 * failure. Before the Vite migration only the Webpack messages were handled,
 * which is why iOS Safari's "Importing a module script failed." slipped through
 * and surfaced as a crash (WATCHTOWER-220 / GAS-988).
 */

// Module-private structural type for narrowing unknown errors; intentionally not
// exported and kept here rather than in a `.types.ts` file as it is not reused.
type SerializableError = {
    name?: string;
    message?: string;
};

const CHUNK_LOAD_ERROR_MESSAGE_PATTERNS: RegExp[] = [
    // Webpack (pre-Vite build).
    /Loading chunk [\s\S]*? failed/i,
    /Loading CSS chunk [\s\S]*? failed/i,
    // Chromium: Chrome, Edge, Android WebView.
    /Failed to fetch dynamically imported module/i,
    // Firefox.
    /error loading dynamically imported module/i,
    // Safari / iOS — the message reported in GAS-988.
    /Importing a module script failed/i,
    // Vite preload helper (CSS).
    /Unable to preload CSS/i,
];

/**
 * Determines whether the given error represents a failed dynamic import / chunk
 * load, across every browser's phrasing of the failure.
 *
 * @param {unknown} error - The error thrown by the router or runtime.
 * @returns {error is SerializableError} `true` if the error is a chunk load error, otherwise `false`.
 */
export const isChunkLoadError = (error: unknown): error is SerializableError => {
    if (!error || typeof error !== "object") {
        return false;
    }

    const { name, message } = error as SerializableError;

    if (name === "ChunkLoadError") {
        return true;
    }

    return typeof message === "string" && CHUNK_LOAD_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * Key/window used to throttle chunk-error reloads. Stored in `sessionStorage`
 * so the throttle survives the reload it triggers.
 */
const RELOAD_TIMESTAMP_KEY = "watchtower:chunk-load-reload-at";
const RELOAD_THROTTLE_MS = 10_000;

/**
 * Indirection around `window.location.reload` so the recovery path can be
 * spied on in tests — jsdom marks `location` and `location.reload` as
 * non-configurable, so they cannot be replaced directly.
 */
export const chunkLoadRecovery = {
    reload: (): void => window.location.reload(),
};

/**
 * Reloads the page to recover from a chunk-load error, at most once per
 * `RELOAD_THROTTLE_MS`. Returns whether a reload was actually triggered.
 *
 * The time window prevents an infinite reload loop when a chunk is genuinely
 * unreachable (offline, a truly missing file) rather than merely stale: the
 * second failure within the window returns `false` and is left for the caller
 * to handle (show the crash UI, report to Sentry). A single stale-deploy reload
 * normally succeeds, so this path rarely gives up in practice.
 *
 * The throttle depends on `sessionStorage` persisting across the reload. If it is
 * unavailable (e.g. Safari private mode) we cannot guarantee we won't loop, so we
 * decline to reload and let the caller show the crash UI (prompting a manual
 * refresh) rather than risk a tight reload loop on a genuinely missing chunk.
 *
 * @returns {boolean} `true` if a reload was triggered, `false` if throttled or unavailable.
 */
export const reloadOnceForChunkError = (): boolean => {
    const now = Date.now();

    let previous: number;
    try {
        previous = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY));
    } catch {
        return false;
    }

    if (Number.isFinite(previous) && previous > 0 && now - previous < RELOAD_THROTTLE_MS) {
        return false;
    }

    try {
        window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(now));
    } catch {
        return false;
    }

    chunkLoadRecovery.reload();
    return true;
};

/**
 * Wraps a route's dynamic `import()` so a failed chunk load recovers by reloading
 * the page instead of crashing. A rejection from `factory()` is, by definition, a
 * failed dynamic import (a stale-deploy chunk miss or a transient network error),
 * so recovery needs no error-message matching — this is React Router's recommended
 * pattern (discussion #10333: the `catch` is scoped to the import, so it does not
 * rely on error-prone string matching across browsers).
 *
 * On failure it reloads once (throttled via {@link reloadOnceForChunkError}). While
 * the page navigates it returns a never-resolving promise so the router/Suspense
 * keeps showing the loading state rather than flashing the crash UI. If the reload
 * is throttled — a prior reload did not recover — it rethrows the **original**
 * error unchanged so the route's error boundary (`SentryErrorFallback`) reports it
 * accurately: a genuine stale-chunk error is still recognised by its message, while
 * a real module-initialisation bug is not mislabelled as a `ChunkLoadError`.
 *
 * Note this deliberately reloads on *any* import rejection, not only recognised
 * chunk messages — that keeps recovery independent of brittle cross-browser error
 * strings. The only cost is one (throttled) reload for the rare case of a module
 * that fails to initialise for a non-chunk reason; the original error still
 * surfaces intact afterwards.
 *
 * @param factory - Produces the dynamic import promise (e.g. `() => import("pages/home")`).
 * @returns The resolved module, or a never-resolving promise while reloading.
 */
export const importOrReload = async <T>(factory: () => Promise<T>): Promise<T> => {
    try {
        return await factory();
    } catch (error) {
        if (reloadOnceForChunkError()) {
            // Page is reloading — hold the loading state until it navigates away.
            return new Promise<T>(() => {});
        }

        throw error;
    }
};
